// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Rule-engine variables (M2-06).
//
// Three scopes and three types, and that is the whole vocabulary. `game` survives the whole
// game, `player` survives that player's ball changes, `ball` resets every ball. Types are
// `int`, `bool` and `set` — no strings, no floats, no objects, no arrays. That poverty is
// deliberate and is what makes the evaluator bounded: there is nothing here to allocate,
// nothing to grow without limit, and nothing an author can nest.
//
// Storage is a flat array per scope instance, indexed by declaration order. Nothing iterates
// a map, so two runtimes walk the same variables in the same sequence.
//
// A `set` is a bitmask in a single binary64, which holds 53 exact integer bits. Members are
// capped well below that at load, so a set can never lose a member to float rounding.

import type { MachineVar, VarScope, VarType } from '../machine/types'

/** Members per set. Far below the 53 exact bits of a binary64, deliberately. */
export const MAX_SET_MEMBERS = 30

interface VarSpec {
  readonly id: string
  readonly scope: VarScope
  readonly type: VarType
  readonly initial: number
  /** Ceiling for `int`. `Infinity` when the definition sets none. */
  readonly max: number
  readonly members: readonly string[]
  readonly memberBit: ReadonlyMap<string, number>
}

export class VarStore {
  private readonly specs: VarSpec[] = []
  private readonly index = new Map<string, number>()
  private readonly playerCount: number

  private game: number[] = []
  private player: number[][] = []
  private ball: number[][] = []

  /** Ids changed since the last drain, in declaration order. Drives `var:` triggers. */
  private readonly dirty: boolean[] = []

  constructor(vars: readonly MachineVar[], playerCount: number) {
    this.playerCount = playerCount
    for (const v of vars) {
      const memberBit = new Map<string, number>()
      const members = v.members ?? []
      for (let i = 0; i < members.length && i < MAX_SET_MEMBERS; i++) {
        memberBit.set(members[i]!, i)
      }
      this.index.set(v.id, this.specs.length)
      this.specs.push({
        id: v.id,
        scope: v.scope,
        type: v.type,
        initial: v.type === 'bool' ? (v.initial === true ? 1 : 0) : typeof v.initial === 'number' ? v.initial : 0,
        max: typeof v.max === 'number' ? v.max : Number.POSITIVE_INFINITY,
        members,
        memberBit,
      })
      this.dirty.push(false)
    }
    this.resetGame()
  }

  get count(): number {
    return this.specs.length
  }

  has(id: string): boolean {
    return this.index.has(id)
  }

  idAt(i: number): string {
    return this.specs[i]!.id
  }

  typeOf(id: string): VarType | undefined {
    const i = this.index.get(id)
    return i === undefined ? undefined : this.specs[i]!.type
  }

  /** Full reset: new game. */
  resetGame(): void {
    this.game = this.specs.map((s) => (s.scope === 'game' ? s.initial : 0))
    this.player = []
    this.ball = []
    for (let p = 0; p < this.playerCount; p++) {
      this.player.push(this.specs.map((s) => (s.scope === 'player' ? s.initial : 0)))
      this.ball.push(this.specs.map((s) => (s.scope === 'ball' ? s.initial : 0)))
    }
    this.clearDirty()
  }

  /** Reset only the `ball`-scoped variables for one player. */
  resetBall(player: number): void {
    const row = this.ball[player]
    if (!row) return
    for (let i = 0; i < this.specs.length; i++) {
      const s = this.specs[i]!
      if (s.scope === 'ball') row[i] = s.initial
    }
  }

  private slot(spec: VarSpec, player: number): { row: number[]; ok: boolean } {
    if (spec.scope === 'game') return { row: this.game, ok: true }
    const row = spec.scope === 'player' ? this.player[player] : this.ball[player]
    return row ? { row, ok: true } : { row: this.game, ok: false }
  }

  get(id: string, player: number): number {
    const i = this.index.get(id)
    if (i === undefined) return 0
    const spec = this.specs[i]!
    const { row, ok } = this.slot(spec, player)
    return ok ? row[i]! : 0
  }

  /**
   * Write a value, clamped to the declared ceiling, and mark the variable changed.
   *
   * Clamping rather than rejecting is right for a game: a bonus multiplier that has reached
   * its maximum should stay at its maximum, not refuse the award and not wrap. The ceiling
   * is part of the machine's design, not an error condition.
   */
  set(id: string, player: number, value: number): void {
    const i = this.index.get(id)
    if (i === undefined) return
    const spec = this.specs[i]!
    const { row, ok } = this.slot(spec, player)
    if (!ok) return
    let next = value
    if (spec.type === 'bool') next = value !== 0 ? 1 : 0
    else if (spec.type === 'int') {
      if (next > spec.max) next = spec.max
      if (next < 0 && spec.initial >= 0) next = next // negatives are legal; only the cap is enforced
    }
    if (row[i] === next) return
    row[i] = next
    this.dirty[i] = true
  }

  add(id: string, player: number, amount: number): void {
    this.set(id, player, this.get(id, player) + amount)
  }

  // --- sets ------------------------------------------------------------------

  addMember(id: string, player: number, member: string): void {
    const i = this.index.get(id)
    if (i === undefined) return
    const bit = this.specs[i]!.memberBit.get(member)
    if (bit === undefined) return
    const mask = 2 ** bit
    const current = this.get(id, player)
    // Bitwise OR would coerce through int32; these are exact binary64 integers, so the
    // arithmetic form is used and stays exact well past 32 members.
    if (Math.floor(current / mask) % 2 === 1) return
    this.set(id, player, current + mask)
  }

  removeMember(id: string, player: number, member: string): void {
    const i = this.index.get(id)
    if (i === undefined) return
    const bit = this.specs[i]!.memberBit.get(member)
    if (bit === undefined) return
    const mask = 2 ** bit
    const current = this.get(id, player)
    if (Math.floor(current / mask) % 2 === 0) return
    this.set(id, player, current - mask)
  }

  hasMember(id: string, player: number, member: string): boolean {
    const i = this.index.get(id)
    if (i === undefined) return false
    const bit = this.specs[i]!.memberBit.get(member)
    if (bit === undefined) return false
    return Math.floor(this.get(id, player) / 2 ** bit) % 2 === 1
  }

  /** Cardinality of a set, or the plain value of anything else. */
  countOf(id: string, player: number): number {
    const i = this.index.get(id)
    if (i === undefined) return 0
    const spec = this.specs[i]!
    const value = this.get(id, player)
    if (spec.type !== 'set') return value
    let n = 0
    for (let b = 0; b < MAX_SET_MEMBERS; b++) {
      if (Math.floor(value / 2 ** b) % 2 === 1) n++
    }
    return n
  }

  membersOf(id: string): readonly string[] {
    const i = this.index.get(id)
    return i === undefined ? [] : this.specs[i]!.members
  }

  // --- change notification -----------------------------------------------------

  /** Ids changed since the last call, in declaration order. Clears the flags. */
  drainChanged(out: string[]): void {
    for (let i = 0; i < this.dirty.length; i++) {
      if (!this.dirty[i]) continue
      this.dirty[i] = false
      out.push(this.specs[i]!.id)
    }
  }

  clearDirty(): void {
    for (let i = 0; i < this.dirty.length; i++) this.dirty[i] = false
  }

  /** Every value, for a snapshot or a state checksum. */
  snapshot(): number[] {
    const out: number[] = [...this.game]
    for (const row of this.player) out.push(...row)
    for (const row of this.ball) out.push(...row)
    return out
  }

  /**
   * Put every value back (M4-28a).
   *
   * The inverse of `snapshot`, walked in the identical order — game row, then each player row,
   * then each ball row. Two traversals that have to agree are two chances to disagree, so the
   * length is checked rather than trusted: a partial restore would leave a variable at its
   * initial value and produce a game that runs perfectly and is not the one that was saved.
   */
  restore(values: readonly number[]): void {
    const expected = this.game.length + this.player.length * (this.player[0]?.length ?? 0) +
      this.ball.length * (this.ball[0]?.length ?? 0)
    if (values.length !== expected) {
      throw new Error(`VarStore.restore: ${values.length} values, expected ${expected}`)
    }
    let at = 0
    for (let i = 0; i < this.game.length; i++) this.game[i] = values[at++]!
    for (const row of this.player) for (let i = 0; i < row.length; i++) row[i] = values[at++]!
    for (const row of this.ball) for (let i = 0; i < row.length; i++) row[i] = values[at++]!
    this.dirty.fill(false)
  }
}
